import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCustomSnackbars } from '../../components/snackbar/SnackBar';
import { useNewEmailSend, useNewEmailVerify } from '../../api/user.api';
import { Button } from 'flowbite-react';
import {
  RESEND_COOLDOWN_SECONDS,
  changeEmailSendErrorMessage,
  changeEmailVerifyErrorMessage,
} from '../password/emailVerifyMessages';

const EmailSend = () => {
  const { mutateAsync: doEmailSend } = useNewEmailSend();
  const { mutateAsync: doEmailVerify } = useNewEmailVerify();

  const [password, setPassword] = useState('');
  const [newEmail, setNewEmail] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [verificationId, setVerificationId] = useState('');
  const [timer, setTimer] = useState(null);
  const [timerDisplay, setTimerDisplay] = useState('');
  const [disabled, setDisabled] = useState(false);
  const [isEmailSendSuccess, setIsEmailSendSuccess] = useState(false);
  const [isPassword, setIsPassword] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const { openSuccessSnackbar, openErrorSnackbar } = useCustomSnackbars();
  // 상태는 다음 렌더에서야 바뀐다. 같은 tick 의 두 번째 탭까지 막으려면 동기 값이어야 한다.
  const verifyingRef = useRef(false);

  const navigate = useNavigate();

  // timer
  useEffect(() => {
    if (timer === null) return;

    const countdown = setInterval(() => {
      setTimer(prevTimer => {
        if (prevTimer <= 1) {
          clearInterval(countdown);
          setTimerDisplay(''); // 타이머 종료 시 표시 제거
          setDisabled(false);
          return null;
        }
        const minutes = Math.floor(prevTimer / 60);
        const seconds = prevTimer % 60;
        setTimerDisplay(`${minutes}:${seconds < 10 ? `0${seconds}` : seconds}`);
        return prevTimer - 1;
      });
    }, 1000);

    return () => clearInterval(countdown);
  }, [timer]);

  // 인증 코드 발송
  const handleSendCode = async () => {
    try {
      setDisabled(true);
      const response = await doEmailSend({ password, newEmail });
      setVerificationId(response.data.verificationId);
      setIsEmailSendSuccess(true);
      openSuccessSnackbar('인증 코드를 보냈습니다.', 2500);
      setTimer(RESEND_COOLDOWN_SECONDS);
    } catch (error) {
      // 원인별로 다른 안내. 서버 원문은 띄우지 않는다.
      openErrorSnackbar(changeEmailSendErrorMessage(error), 2500);
      setIsEmailSendSuccess(false);
      setDisabled(false);
    }
  };

  // 서버에 코드가 남아 있지 않으면(시도 초과·만료) 재발송 잠금을 푼다
  const releaseResendLock = () => {
    setTimer(null);
    setTimerDisplay('');
    setDisabled(false);
  };

  // 인증 코드 입력 감지
  const handleVerificationCode = e => {
    setVerificationCode(e.target.value);
  };

  //인증 코드 확인
  const handleButton = async () => {
    // 한 번 더 탭하면 서버의 인증 코드 시도 횟수(5회)만 줄어든다
    if (verifyingRef.current) return;
    verifyingRef.current = true;
    setVerifying(true);
    try {
      await doEmailVerify({
        verificationId: verificationId,
        verifyCode: verificationCode,
      });
      openSuccessSnackbar('이메일을 변경했습니다.', 2500);
      navigate('/mypage');
    } catch (error) {
      const { message, resetResend } = changeEmailVerifyErrorMessage(error);
      if (resetResend) releaseResendLock();
      openErrorSnackbar(message, 2500);
    } finally {
      verifyingRef.current = false;
      setVerifying(false);
    }
  };

  return (
    <div className="flex flex-col items-center w-screen p-5">
      <h1
        style={{ fontWeight: 450 }}
        className="text-2xl text-center mt-10 mb-5">
        이메일 재설정을 위한 본인 인증
      </h1>
      <div className="mt-5 mb-10 text-center" style={{ color: '#9D9FA2' }}>
        <p className="break-keep">
          이메일 재설정을 위해선 비밀번호와 이메일을 통한 본인 인증이 필요합니다
        </p>
        <p className="mt-2 break-keep">
          본인의 비밀번호와 새 이메일 주소를 입력하면 해당하는 이메일로 인증
          코드가 전송됩니다
        </p>
      </div>
      {/* 비밀번호 입력 */}
      <div className="flex items-center justify-center border rounded-lg p-2 mb-5 w-full max-w-md">
        <div className="relative flex-grow">
          <input
            onChange={e => {
              setPassword(e.target.value);
              setIsPassword(e.target.value.trim().length > 0);
            }}
            type="password"
            className="focus:outline-none w-full p-2 text-sm border-none"
            placeholder="비밀번호 입력"
            value={password}
          />
        </div>
      </div>
      {/* 새 이메일 입력 */}
      <div className="flex items-center justify-center border rounded-lg p-2 mb-5 w-full max-w-md">
        <div className="relative flex-grow">
          <input
            onChange={e => setNewEmail(e.target.value)}
            type="email"
            className="focus:outline-none w-full p-2 text-sm border-none"
            placeholder="새 이메일 입력"
            value={newEmail}
          />
          {timerDisplay && (
            <span className="absolute right-2 top-1/2 -translate-y-1/2 text-red-500 text-sm">
              {timerDisplay}
            </span>
          )}
        </div>
        <button
          id="button"
          onClick={handleSendCode}
          className={`text-white p-2 rounded-lg ml-2 ${disabled || !isPassword ? 'opacity-50 cursor-not-allowed' : ''}`}
          style={{ backgroundColor: '#1e2332' }}
          disabled={disabled || !isPassword}>
          인증 코드 발송
        </button>
      </div>
      {/* 인증 코드 입력 */}
      {isEmailSendSuccess && (
        <div className="flex items-center justify-center border rounded-lg p-2 mb-5 w-full max-w-md">
          <input
            onChange={handleVerificationCode}
            className="focus:outline-none flex-grow p-2 text-sm border-none"
            placeholder="인증 코드 입력"></input>
        </div>
      )}
      {/* 확인 버튼 */}
      <Button
        onClick={handleButton}
        style={{ backgroundColor: '#1e2332' }}
        id="btn"
        disabled={verifying}
        className="cursor-pointer text-white w-full max-w-xs">
        {verifying ? '확인 중...' : '확인'}
      </Button>
    </div>
  );
};

export default EmailSend;
